/**
 * PatientConsentModal — R5 edit modal for the proband's (patient) consents.
 *
 * Mirrors the comparator consent flow: the summary `<PatientConsentCard>` opens
 * this focused modal to edit Incidental Findings / Research Contact / NY Sample
 * Retention. It reuses the existing editable `<ProbandConsentSections>` controls
 * against a local draft so the inline (legacy) and modal (R5) editors stay in
 * sync. Edits commit on Done; Discard reverts to the open-time baseline.
 *
 * Presentation-only: the parent owns canonical state and applies the returned
 * patch (typically `dispatch(mergePatient(patch))`).
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { Modal } from '@/components/common/Modal'
import type { ComparatorParentConsentConfigSlice } from '@/components/order/comparatorParentConsentConfig'
import { comparatorConsentAnyApplicable } from '@/components/order/comparatorParentConsentConfig'
import { ProbandConsentSections, type ProbandConsentPatch } from '@/components/order/ProbandConsentSections'
import type { CanonicalPatient, Comparator } from '@/ordering/canonicalOrder'
import { probandIncidentalFindingsLocksComparator } from '@/utils/comparatorIncidentalFindings'

interface PatientConsentModalProps {
  isOpen: boolean
  onClose: () => void
  configs: ComparatorParentConsentConfigSlice
  patient: CanonicalPatient | null | undefined
  /**
   * Comparators as of open time. Used to detect when a proband opt-out will
   * (via the `mergePatient` reducer's GEN-3484 cascade) auto-opt-out a
   * comparator that had opted in, so the "automatically opted out" notice can
   * be shown on the incidental-findings section.
   */
  comparators?: ReadonlyArray<Comparator> | null
  onSave: (patch: ProbandConsentPatch) => void
  /**
   * When true, show inline validation errors on unanswered `requireInteraction`
   * consent sections (e.g. the modal was opened from a blocked submit / a
   * needs-attention card). Defaults to false.
   */
  showValidationErrors?: boolean
}

function patientToDraft(patient: CanonicalPatient | null | undefined): ProbandConsentPatch {
  return {
    incidentalFindingsSelection: patient?.incidentalFindingsSelection ?? null,
    newYorkSampleRetentionOptIn: patient?.newYorkSampleRetentionOptIn ?? null,
    researchContactConsent: patient?.researchContactConsent ?? null,
  }
}

export function PatientConsentModal({
  isOpen,
  onClose,
  configs,
  patient,
  comparators,
  onSave,
  showValidationErrors = false,
}: PatientConsentModalProps) {
  const [draft, setDraft] = useState<ProbandConsentPatch>(() => patientToDraft(patient))

  // Hydrate the draft from canonical only on open so in-progress edits aren't
  // clobbered by re-renders while the modal is open.
  const patientRef = useRef(patient)
  patientRef.current = patient
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen) {
      if (!wasOpenRef.current) setDraft(patientToDraft(patientRef.current))
      wasOpenRef.current = true
    } else {
      wasOpenRef.current = false
    }
  }, [isOpen])

  // ProbandConsentSections expects a CanonicalPatient; only the three consent
  // fields are read, so overlay the draft on the live patient.
  const draftPatient = useMemo(
    () => ({ ...(patient ?? {}), ...draft }) as CanonicalPatient,
    [patient, draft]
  )

  // GEN-3484: a proband opt-out cascades (via the `mergePatient` reducer) to
  // force every comparator's incidental findings to opt-out. Surface the
  // "automatically opted out" notice when the current (draft) selection is
  // opt-out and at least one comparator had opted in — reading the live,
  // pre-cascade comparators captured at open time.
  const comparatorIncidentalSelectionMayHaveBeenModified = useMemo(
    () =>
      probandIncidentalFindingsLocksComparator(draft.incidentalFindingsSelection) &&
      (comparators ?? []).some((c) => c?.secondaryConsent?.includeIncidentalFindings === true),
    [draft.incidentalFindingsSelection, comparators]
  )

  const handleDone = () => {
    onSave(draft)
    onClose()
  }

  const handleDiscard = () => {
    setDraft(patientToDraft(patientRef.current))
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDone}
      title="Additional Options"
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-4">
          <button
            type="button"
            onClick={handleDiscard}
            className="rounded-full border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-medium leading-5 text-text-body shadow-xs transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            Discard Changes
          </button>
          <button
            type="button"
            onClick={handleDone}
            className="rounded-full bg-brand-600 px-4 py-2.5 text-sm font-medium leading-5 text-white shadow-xs transition-colors hover:bg-brand-700"
          >
            Done
          </button>
        </div>
      }
    >
      {comparatorConsentAnyApplicable(configs) ? (
        <ProbandConsentSections
          configs={configs}
          patient={draftPatient}
          onPatch={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
          comparatorIncidentalSelectionMayHaveBeenModified={
            comparatorIncidentalSelectionMayHaveBeenModified
          }
          showValidationErrors={showValidationErrors}
        />
      ) : (
        <span className="text-sm text-text-body-subtle dark:text-gray-500">
          No additional options are available for this order.
        </span>
      )}
    </Modal>
  )
}
